import { Box, LinearProgress } from "@mui/material";
import { useMemo } from "react";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import Text from "../../text/Text";
import {
  btc,
  countLimit as countLimitTxt,
  daily,
  ggc,
  limit,
  receive,
  sats,
  send,
  swapConvert,
  unlimited,
  usdt,
  usedOutofMax,
  weekly,
  XAUT,
} from "../../messages";
import { displayCurrency, showAmount } from "../../constants";
import { currencyImages } from "../../images";

const formattedAmount = (currency, amount) => {
  const displayedAmt = showAmount({
    amount,
    currency,
    targetedCurrency: currency === sats ? btc : currency,
    showCommaSeparated: true,
  });

  switch (currency) {
    case ggc:
      return `☆${displayedAmt}`;
    case usdt:
      return `₮${displayedAmt}`;
    case XAUT:
      return `℥${displayedAmt}`;
    case sats:
      const btcAmount = Number(displayedAmt);
      if (btcAmount < 0.001 && btcAmount > 0) {
        return `<₿${0.001}`;
      }
      return `₿${btcAmount}`;
    default:
      return `$${displayedAmt}`;
  }
};

const currencyText = (targetCurrency) => {
  return (
    <Text
      sx={{ display: "flex", alignItems: "center", gap: "4px" }}
      font="semibold"
      size={16}
    >
      <img
        src={currencyImages[targetCurrency]}
        height={20}
        width={20}
        alt={targetCurrency}
      />
      {displayCurrency(targetCurrency)}
    </Text>
  );
};

const LimitProgressBar = ({
  limitType,
  isLimited,
  currency,
  amountUsed,
  amountLimit,
  countUsed,
  countLimit,
}) => {
  let progressPercent = isLimited
    ? Math.min((amountUsed * 100) / amountLimit, 100)
    : 100;

  progressPercent =
    progressPercent > 0 && progressPercent < 1 ? 1 : progressPercent;

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      marginTop={2}
    >
      <Box>
        <Text size={14} font="semibold">
          {limitType}
        </Text>
        <Text size={14} font="semibold" className="grey-text">
          {usedOutofMax}
        </Text>
      </Box>
      <Box width={"75%"}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Text
            font="medium"
            size={14}
            sx={{ marginBottom: "4px" }}
            className="grey-text"
          >
            {isLimited ? (
              <>
                <span style={{ color: "black" }}>
                  {formattedAmount(currency, amountUsed)}
                </span>
                /{formattedAmount(currency, amountLimit)}
              </>
            ) : (
              unlimited
            )}
          </Text>
          {countLimit && (
            <Text font="medium" size={14} className="grey-text">
              {countLimitTxt(countUsed, countLimit)}
            </Text>
          )}
        </Box>
        <LinearProgress
          variant="determinate"
          value={progressPercent}
          sx={{
            backgroundColor: "#f0f0f0",
            "& .MuiLinearProgress-bar": {
              backgroundColor: limitType === daily ? "#2A67FF" : "#F1C40F",
            },
          }}
        />
      </Box>
    </Box>
  );
};

const LimitCard = ({ type, data }) => {
  const currency = useMemo(() => {
    switch (type) {
      case send:
        return data?.currency;
      case swapConvert:
        return data?.target_currency_swap_out;
      default:
        return data?.target_currency;
    }
  }, [data]);

  const cardHeader = useMemo(() => {
    switch (type) {
      case send:
        return (
          <Text size={16} font="bold">
            {limit}
          </Text>
        );
      case swapConvert:
        return (
          <Box display="flex" alignItems="center" gap={1}>
            {currencyText(data?.target_currency_swap_out)}
            <ArrowForwardIcon sx={{ height: "14px", width: "14px" }} />
            {currencyText(data?.target_currency_swap_in)}
          </Box>
        );
      default:
        return currencyText(currency);
    }
  }, [data]);

  const dailyProgressBarProps = {
    limitType: daily,
    isLimited: type !== receive,
    currency: currency,
    amountUsed: data?.amount_limit_daily_used,
    amountLimit: data?.amount_limit_daily,
    countLimit: data?.count_limit_daily,
    countUsed: data?.count_limit_daily_used,
  };

  const weeklyProgressBarProps = {
    limitType: weekly,
    isLimited: type !== receive,
    currency: currency,
    amountUsed: data?.amount_limit_weekly_used,
    amountLimit: data?.amount_limit_weekly,
    countLimit: data?.count_limit_weekly,
    countUsed: data?.count_limit_weekly_used,
  };

  return (
    <Box className="limit-card">
      {cardHeader}
      <LimitProgressBar {...dailyProgressBarProps} />
      <LimitProgressBar {...weeklyProgressBarProps} />
    </Box>
  );
};

export default LimitCard;
